import { Component, OnInit, ViewChild, inject, ChangeDetectionStrategy } from '@angular/core';

import { TranslatePipe } from '@ngx-translate/core';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { Router } from '@angular/router';
import { ChatFacade } from '@cnp/services/chat-facade';
import { ChatRoomView } from '@cnp/common';
import { AppRoutes } from '@cnp/common';
import { CommonHelper } from '@meplis/common';
import { ChatButtonComponent } from '../chat-button/chat-button.component';
import { ChatRoomListComponent } from '../chat-room-list/chat-room-list.component';

/** How many conversations the header preview shows before "see all". */
const PREVIEW_LIMIT = 6;

/**
 * Header entry point on desktop: a preview of the most recent conversations
 * with a link into the full /chat page -- the same shape as the existing
 * notification menu, and what LinkedIn does with messaging.
 */
@Component({
  selector: 'app-chat-menu',
  standalone: true,
  imports: [
    TranslatePipe,
    MatMenuModule,
    ChatButtonComponent,
    ChatRoomListComponent
],
  templateUrl: './chat-menu.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './chat-menu.component.scss',
})
export class ChatMenuComponent implements OnInit {
  private _chatFacade = inject(ChatFacade);
  private _router = inject(Router);

  // The conversation rows come from the shared, non-Material chat-room-list
  // component, so they're plain buttons rather than `mat-menu-item`s -- which
  // means Material's own close-on-item-click never fires for them. Closing
  // has to happen explicitly.
  @ViewChild(MatMenuTrigger) private _menuTrigger!: MatMenuTrigger;

  public readonly isLoading = this._chatFacade.isLoadingRooms;
  public readonly unreadCount = this._chatFacade.totalUnread;

  public get rooms(): ChatRoomView[] {
    return this._chatFacade.rooms().slice(0, PREVIEW_LIMIT);
  }

  public ngOnInit(): void {
    this._chatFacade.loadRooms().subscribe({
      error: (error) =>
        console.warn('[ChatMenuComponent] Unable to load conversations', error),
    });
  }

  public onRoomSelected(room: ChatRoomView): void {
    this._menuTrigger.closeMenu();
    this._router.navigate([
      CommonHelper.buildFullRouteUrl(AppRoutes['CHAT_ROOM'].fullPath, [room.id]),
    ]);
  }

  public onSeeAll(): void {
    this._menuTrigger.closeMenu();
    this._router.navigate([AppRoutes['CHAT'].fullPath]);
  }
}
